import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SendIcon from '@mui/icons-material/Send';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AxiosConfig from '../../../../../../../config/AxiosConfig';
import languages from '../../../../../../helpers/utils/languages';
import useGetSavOrdersByOrderId from '../../../../../../hooks/orders/useGetSavOrdersByOrderId';
import { setSnackBar } from '../../../../../../states/app/staticSlice';
import useResentPaymentLink from '../../../../../../hooks/orders/useResentPaymentLink';
import ArrowReturnIcon from '../../../../../svg-icons/ArrowReturnIcon';
import CancelOrderForm from '../Cancels/CancelOrderForm.jsx';
import UpdateSavShipping from './UpdateSavShipping';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const ManageSav = ({
  orderDetails,
  setSnackBarProps,
  handleShowComponent,
  profile,
}) => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.static.users);
  const [currentSavOrderRef, setCurrentSavOrderRef] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [statusUpdateLoadingRef, setStatusUpdateLoadingRef] = useState(null);
  const [savToValidate, setSavToValidate] = useState(null);
  // SAV en cours d'annulation : tant qu'elle est définie, on affiche le
  // formulaire d'annulation à la place de la liste des SAV.
  const [savToCancel, setSavToCancel] = useState(null);

  const orderId = useMemo(() => orderDetails?._id, [orderDetails]);

  const { savOrders, isLoading, error, mutate } =
    useGetSavOrdersByOrderId(orderId);
  const { resentPaymentLink } = useResentPaymentLink();

  const getAuthorName = (savDatasFromBO) => {
    const user = users?.find(
      (user) => user.email === savDatasFromBO.created_by
    );
    return user
      ? `${user.firstName} ${user.lastName}`
      : savDatasFromBO.created_by;
  };

  const getCountryName = (countryCode) => {
    return (
      languages.find((country) => country.code2 === countryCode)?.country ||
      countryCode
    );
  };

  const handleChangeTab = (event, newValue) => {
    console.log('newValue', newValue);
    setCurrentSavOrderRef(newValue);
  };

  const handleResentaymentLink = async (savOrderRef) => {
    try {
      setEmailSending(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await resentPaymentLink(savOrderRef, {
        ticketRef: orderDetails?.ticket?.reference,
        author: profile?._id,
      });
      setEmailSending(false);
      setSnackBarProps({
        isOpen: true,
        message: "L'email avec le lien de paiement a été renvoyé avec succès",
        variant: 'success',
      });
    } catch (error) {
      console.error(error);
      setEmailSending(false);
      setSnackBarProps({
        isOpen: true,
        message: "Une erreur est survenue lors du renvoi de l'email",
        variant: 'error',
      });
    }
  };

  const updateSavStatus = async (savOrder, nextStatusCode) => {
    if (!savOrder?._id || !orderDetails?.ticket?._id || !profile?._id) {
      dispatch(
        setSnackBar({
          open: true,
          message: 'Impossible de mettre à jour le statut de la SAV.',
          color: 'error',
        })
      );
      return;
    }

    const nextStatusLabel =
      nextStatusCode === 'WAITING_SC' ? 'en attente SC' : 'validée';
    const authorName = `${profile?.firstName || ''} ${
      profile?.lastName || ''
    }`.trim();
    const note = `==> Mise à jour de la SAV "${savOrder?.reference}" au statut "${nextStatusLabel}"
==> Modifié par : ${authorName || 'Utilisateur inconnu'}
==> Commentaire : Aucun commentaire fourni`;

    try {
      setStatusUpdateLoadingRef(savOrder.reference);
      const response = await AxiosConfig.create(
        'put',
        `/orders/${savOrder._id}/status/`
      )
        .call({
          statusCode: nextStatusCode,
          userId: profile._id,
          ticketId: orderDetails.ticket._id,
          note,
        })
        .getResponseData();

      if (!response || response.code >= 300) {
        throw new Error('Failed to update SAV status');
      }

      await mutate();
      dispatch(
        setSnackBar({
          open: true,
          message: `La SAV ${savOrder.reference} est maintenant ${nextStatusLabel}.`,
          color: 'success',
        })
      );
    } catch (error) {
      dispatch(
        setSnackBar({
          open: true,
          message: 'Une erreur est survenue lors de la mise à jour du statut.',
          color: 'error',
        })
      );
    } finally {
      setStatusUpdateLoadingRef(null);
    }
  };

  const handleToggleSavStatus = async (savOrder) => {
    // Harmonisation UX avec l'écran de mise à jour de statut : le passage
    // WAITING_SC -> VALIDATED demande une confirmation explicite.
    if (savOrder?.status?.code === 'WAITING_SC') {
      setSavToValidate(savOrder);
      return;
    }
    await updateSavStatus(savOrder, 'WAITING_SC');
  };

  const handleConfirmSavValidation = async () => {
    if (!savToValidate) {
      return;
    }
    const currentSav = savToValidate;
    setSavToValidate(null);
    await updateSavStatus(currentSav, 'VALIDATED');
  };

  useEffect(() => {
    if (isLoading || !savOrders?.length) return;
    setCurrentSavOrderRef(savOrders[0].reference);
  }, [savOrders, isLoading]);

  const renderFallback = (message, isError = false) => (
    <Card elevation={5} sx={{ p: 3, mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography color={isError ? 'error' : 'text.secondary'}>
          {message}
        </Typography>
        <Button onClick={() => handleShowComponent(null)} variant="outlined">
          <ArrowReturnIcon fontSize="small" />
          <span style={{ marginLeft: 4 }}>Retour</span>
        </Button>
      </Box>
    </Card>
  );

  // Garde explicite : sans identifiant de commande valide, aucune requête n'est
  // émise (clé SWR nulle). On évite ainsi le spinner infini sur une commande
  // introuvable / non chargée.
  if (!orderId) {
    return renderFallback(
      'Commande introuvable : impossible de charger les SAVs.',
      true
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return renderFallback(
      'Une erreur est survenue lors du chargement des SAVs.',
      true
    );
  }

  if (!savOrders?.length) {
    return renderFallback('Aucune SAV à gérer pour cette commande.');
  }

  // `savOrders` est chargé mais l'onglet courant n'est pas encore initialisé :
  // état transitoire avant que l'effet ne fixe `currentSavOrderRef`.
  if (!currentSavOrderRef) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Annulation d'une commande SAV : on réutilise le formulaire d'annulation
  // standard en lui passant la commande SAV ciblée. Le retour (handleShowComponent
  // null) ramène à la liste des SAV et rafraîchit les données plutôt que de
  // fermer complètement l'écran "Gérer les SAVs".
  if (savToCancel) {
    return (
      <CancelOrderForm
        order={savToCancel}
        ticket={orderDetails?.ticket}
        profile={profile}
        handleShowComponent={(nextComponent) => {
          if (nextComponent === null) {
            setSavToCancel(null);
            mutate();
            return;
          }
          handleShowComponent(nextComponent);
        }}
      />
    );
  }

  return (
    <>
      <Card
        elevation={5}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          padding: 2,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <SettingsApplicationsIcon
              style={{ color: '#10407B' }}
              fontSize="small"
            />
            <Typography color="primary">Gérer les SAVs</Typography>
          </Box>
          <Button
            onClick={() => handleShowComponent(null)}
            variant="outlined"
            size="small"
            sx={{ position: 'absolute', top: 16, left: 16 }}
          >
            <ArrowReturnIcon fontSize="small" />
            <span style={{ marginLeft: 4, fontSize: '0.9em' }}>Retour</span>
          </Button>
        </Box>
      </Card>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentSavOrderRef} onChange={handleChangeTab}>
            {savOrders?.map((savOrder) => (
              <Tab
                key={savOrder.reference}
                label={savOrder.reference}
                value={savOrder.reference}
                {...a11yProps(savOrder.reference)}
              />
            ))}
          </Tabs>
        </Box>
        {savOrders.map((savOrder) => (
          <CustomTabPanel
            value={currentSavOrderRef}
            index={savOrder.reference}
            key={savOrder.reference}
          >
            <Card elevation={5} sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontWeight: 'bold' }}
                >
                  {savOrder?.savDatasFromBO?.reference}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={
                      savOrder.savDatasFromBO.is_payable
                        ? `SAV Payante ${savOrder.savDatasFromBO.amount_ttc} ${getSymbolFromCurrency(
                            savOrder.savDatasFromBO.currency
                          )}`
                        : 'SAV Gratuite'
                    }
                    color={
                      savOrder.savDatasFromBO.is_payable ? 'warning' : 'success'
                    }
                    variant="outlined"
                  />
                  {savOrder?.paymentStatus && (
                    <Chip
                      label={savOrder.paymentStatus.name || 'Statut inconnu'}
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Typography variant="body2">
                  Créée par{' '}
                  <strong>{getAuthorName(savOrder.savDatasFromBO)}</strong>
                </Typography>
                <Typography variant="body2">
                  le{' '}
                  {format(
                    new Date(savOrder.savDatasFromBO.created_at),
                    'dd MMMM yyyy à HH:mm',
                    {
                      locale: fr,
                    }
                  )}
                </Typography>
              </Box>

              {savOrder.savDatasFromBO.is_payable && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Montant :{' '}
                    <strong>
                      {savOrder.savDatasFromBO.amount_ttc}{' '}
                      {savOrder.savDatasFromBO.currency.toUpperCase()}
                    </strong>
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>
                Pièces / colis
              </Typography>
              <Box sx={{ mb: 2 }}>
                {savOrder.savDatasFromBO.lines.map((line, index) => (
                  <Typography key={index} variant="body2">
                    {`${line.quantity} x `}
                    <strong>{line.reference}</strong>
                    {line?.title && ` - ${line?.title}`}
                  </Typography>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Adresse de livraison
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.shipping_address.first_name}{' '}
                    {savOrder.savDatasFromBO.shipping_address.last_name}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.shipping_address.address_1}
                  </Typography>
                  {savOrder.savDatasFromBO.shipping_address.address_2 && (
                    <Typography variant="body2">
                      {savOrder.savDatasFromBO.shipping_address.address_2}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.shipping_address.postal_code}{' '}
                    {savOrder.savDatasFromBO.shipping_address.city}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.shipping_address.country && (
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <span
                          className={`fi fi-${savOrder.savDatasFromBO.shipping_address.country?.toLowerCase() || ''}`}
                        />
                        <Typography variant="body2">
                          {getCountryName(
                            savOrder.savDatasFromBO.shipping_address.country
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.shipping_address.phone}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Adresse de facturation
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.billing_address.first_name}{' '}
                    {savOrder.savDatasFromBO.billing_address.last_name}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.billing_address.address_1}
                  </Typography>
                  {savOrder.savDatasFromBO.billing_address.address_2 && (
                    <Typography variant="body2">
                      {savOrder.savDatasFromBO.billing_address.address_2}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.billing_address.postal_code}{' '}
                    {savOrder.savDatasFromBO.billing_address.city}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.billing_address.country && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <span
                          className={`fi fi-${savOrder.savDatasFromBO.billing_address.country?.toLowerCase() || ''}`}
                        />
                        {getCountryName(
                          savOrder.savDatasFromBO.billing_address.country
                        )}
                      </Box>
                    )}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.billing_address.phone}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <UpdateSavShipping
                savDatasFromBO={savOrder.savDatasFromBO}
                setSnackBarProps={setSnackBarProps}
                profile={profile}
                ticket={orderDetails?.ticket}
              />
              <Divider sx={{ my: 2 }} />
              {savOrder.savDatasFromBO.is_payable &&
                savOrder?.paymentStatus?.boV2Code !== 'payment_free' &&
                savOrder?.paymentStatus?.boV2Code !== 'payment_captured' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{ fontWeight: 'bold' }}
                        gutterBottom
                      >
                        Lien de paiement
                      </Typography>
                      <Button
                        variant="outlined"
                        color="primary"
                        endIcon={
                          emailSending ? (
                            <CircularProgress size={16} />
                          ) : (
                            <SendIcon />
                          )
                        }
                        disabled={
                          emailSending ||
                          savOrder?.paymentStatus?.boV2Code ===
                            'payment_captured'
                        }
                        onClick={() =>
                          handleResentaymentLink(savOrder.reference)
                        }
                      >
                        Renvoyer le lien de paiement
                      </Button>
                    </Box>
                  </>
                )}

              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}
              >
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => handleShowComponent(null)}
                >
                  Retour
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {['VALIDATED', 'WAITING_SC'].includes(
                    savOrder?.status?.code
                  ) && (
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled={statusUpdateLoadingRef === savOrder?.reference}
                      onClick={() => handleToggleSavStatus(savOrder)}
                    >
                      {statusUpdateLoadingRef === savOrder?.reference
                        ? 'Mise à jour...'
                        : savOrder?.status?.code === 'VALIDATED'
                          ? 'Passer en attente SC'
                          : 'Valider la SAV'}
                    </Button>
                  )}
                  {/* Le <span> permet à l'info-bulle de s'afficher même quand le
                      bouton est désactivé (contrainte MUI). */}
                  <Tooltip
                    title={
                      savOrder?.isCancellable
                        ? ''
                        : savOrder?.status?.code === 'VALIDATED'
                          ? "Cette SAV est validée. Passez-la explicitement en 'En attente SC' avant de l'annuler."
                          : "Cette SAV ne peut pas être annulée dans son statut actuel (envoyée au STO, livrée ou déjà annulée)."
                    }
                    arrow
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<HighlightOffIcon />}
                        disabled={!savOrder?.isCancellable}
                        onClick={() => setSavToCancel(savOrder)}
                      >
                        Annuler la SAV
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Card>
          </CustomTabPanel>
        ))}
      </Box>
      <Dialog open={Boolean(savToValidate)} onClose={() => setSavToValidate(null)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir passer la SAV{' '}
            <strong>{savToValidate?.reference || ''}</strong> au statut
            {' '}<strong>Validée</strong> ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setSavToValidate(null)}
            color="primary"
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmSavValidation}
            disabled={statusUpdateLoadingRef === savToValidate?.reference}
          >
            {statusUpdateLoadingRef === savToValidate?.reference
              ? 'Mise à jour...'
              : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageSav;
